'use client';

// ─────────────────────────────────────────────────────────────────────────────
// TopAppBar — the slim page header for the PM dashboard.
//
// Extracted from DashboardInteractive so the orchestrator stays focused on
// tab state and modal plumbing. Renders:
//   - Optional "← Today" back chip when not on the Today tab. The chip is
//     the primary visual affordance for getting back to the landing tab —
//     the BottomTabBar's Today entry works too, but a top-left back button
//     reads more like "home" to users coming off other dashboards.
//   - Title (per active tab) + welcome line + supabase connection dot
//   - Photo Approvals link as a 44px button
//
// The "tabTitle" prop lets each tab override the H1 — e.g. "Today",
// "Inspections", "Tickets" — so the user always knows where they are.
// ─────────────────────────────────────────────────────────────────────────────

import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import SupabaseStatusDot from '@/components/common/SupabaseStatusDot';

interface TopAppBarProps {
  firstName: string;
  tabTitle: string;
  /** Show the "← Today" back chip + wire it to this handler. Omit on the Today tab itself. */
  onBackToHome?: () => void;
}

export default function TopAppBar({ firstName, tabTitle, onBackToHome }: TopAppBarProps) {
  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
      <div className="min-w-0 flex items-center gap-3">
        {onBackToHome && (
          <button
            onClick={onBackToHome}
            className="flex items-center gap-1.5 px-3 rounded-lg bg-muted text-foreground hover:bg-border transition-standard press-scale flex-shrink-0"
            style={{ minHeight: '44px' }}
            aria-label="Back to Today tab"
          >
            <Icon name="ArrowLeftIcon" size={16} variant="outline" />
            <span className="font-body text-sm hidden sm:inline">Today</span>
          </button>
        )}
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold text-foreground truncate">{tabTitle}</h1>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <p className="font-caption text-sm text-muted-foreground">
              Welcome back, {firstName} · {today}
            </p>
            <span className="text-gray-300 text-xs">·</span>
            <SupabaseStatusDot showEscalationsAge={true} />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Link
          href="/photo-gallery-approval-workflow-system"
          className="flex items-center gap-2 px-4 bg-muted text-foreground rounded-lg font-body text-sm hover:bg-border transition-standard press-scale"
          style={{ minHeight: '44px' }}
        >
          <Icon name="PhotoIcon" size={16} variant="outline" />
          <span className="hidden sm:inline">Photo Approvals</span>
        </Link>
      </div>
    </div>
  );
}
